import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import ENDPOINTS from '../../../Constants/ApiEndpoints';
import './PharmacySupplierPayment.css';
import DateRangePickerComponent from '../../../CommonComponents/DateRangePickerComponent';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Pagination, PaginationItem } from '@mui/material';
import {
  FaSyncAlt,
  FaChevronLeft,
  FaCreditCard,
  FaTimes,
  FaFileInvoiceDollar
} from 'react-icons/fa';

/**
 * PharmacySupplierPayment Component (Outpatient Enterprise Aligned)
 * 
 * Synchronized with OP screens architecture:
 * 1. Top Breadcrumb Bar: Dashboard > pharmacySupplierPayment with Cyan-Blue "< Back" button.
 * 2. Active Supplier Banner: Highlighted supplier name chip.
 * 3. Unified Master Card (.unified-master-card):
 *    - Master Accent Bar on top.
 *    - Master Toolbar Header:
 *      - Fused Search Group (GRN No / Invoice No selector + search input).
 *      - Common Calendar Utility (DateRangePickerComponent from CommonComponents).
 *      - Stat Box, Payment Amount calculation, and "Make Payment" CTA.
 *    - Master Tabs & Status Chips Bar:
 *      - Tabs: "Purchase Entries ({count})" and "Payments History ({count})".
 *      - Status Chips: Pending, Paid, Partial.
 *    - Master Data Table (.master-data-table):
 *      - Purchase Entries view with clearedAmount indicator "[cleared Amount - X Rs]".
 *      - Payments view with transaction history.
 *    - Master Pagination Footer (.master-pagination-footer):
 *      - Material-UI <Pagination> with Previous & Next labels.
 *      - Page indicator: "Showing page X of Y (N records)".
 * 4. Make Payment modal dialog for recording new payments against supplier invoices.
 * 
 * Includes explanatory comments throughout.
 */
const PharmacySupplierPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { supplierId } = useParams();

  // Retrieve supplier details passed via React Router state, or fallback to URL param / default
  const supplierFromState = location.state?.supplier;
  const supplierName =
    supplierFromState?.name ||
    (supplierId ? `Supplier #${supplierId}` : 'Selected Supplier');
  const resolvedSupplierId = supplierFromState?.id || supplierId;

  // ─── Active Tab State ──────────────────────────────────────────────────────
  // 'purchase_entries' | 'payments'
  const [activeTab, setActiveTab] = useState('purchase_entries');

  // ─── Search & Date Filter States (Common Calendar Integration) ─────────────
  const [searchType, setSearchType] = useState('GRN No');
  const [searchValue, setSearchValue] = useState('');
  const [startDate, setStartDate] = useState(new Date(2026, 0, 1));
  const [endDate, setEndDate] = useState(new Date(2026, 11, 31));
  const [isRefreshing, setIsRefreshing] = useState(false);

  // ─── Selection & Payment Calculation States ────────────────────────────────
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  const [isMakePaymentModalOpen, setIsMakePaymentModalOpen] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);

  // ─── Pagination States (Standard OP Pagination) ─────────────────────────────
  const [page, setPage] = useState(1);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const pageSize = 10;

  // ─── Mock Data for Purchase Entries ────────────────────────────────────────
  const [purchaseEntries, setPurchaseEntries] = useState([]);

  // Payment records for the "Payments" tab
  const [paymentsHistory, setPaymentsHistory] = useState([]);

  // Form state for Make Payment modal
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMode: 'Bank Transfer',
    referenceNo: '',
    notes: ''
  });

  /**
   * Handles date range updates from DateRangePickerComponent
   */
  const handleDateChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
    setPage(1);
    setPaymentsPage(1);
  };

  /**
   * Filtered purchase entries based on the GRN/Invoice search input and date range
   */
  const filteredEntries = useMemo(() => {
    return purchaseEntries.filter((entry) => {
      // 1. Search filter
      if (searchValue.trim()) {
        const term = searchValue.toLowerCase();
        if (searchType === 'GRN No' && !entry.grnNo.toLowerCase().includes(term)) {
          return false;
        } else if (searchType === 'Invoice No' && !entry.invoiceNumber.toLowerCase().includes(term)) {
          return false;
        }
      }

      // 2. Date filter
      if (entry.date && startDate && endDate) {
        const d = new Date(entry.date);
        if (d < startDate || d > endDate) return false;
      }

      return true;
    });
  }, [purchaseEntries, searchType, searchValue, startDate, endDate]);

  /**
   * Filtered payments history based on date range
   */
  const filteredPayments = useMemo(() => {
    return paymentsHistory.filter((pay) => {
      if (pay.date && startDate && endDate) {
        const d = new Date(pay.date);
        if (d < startDate || d > endDate) return false;
      }
      return true;
    });
  }, [paymentsHistory, startDate, endDate]);

  /**
   * Pagination calculations
   */
  const totalPages = Math.ceil(filteredEntries.length / pageSize) || 1;
  const paginatedEntries = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredEntries.slice(start, start + pageSize);
  }, [filteredEntries, page, pageSize]);

  const paymentsTotalPages = Math.ceil(filteredPayments.length / pageSize) || 1;
  const paginatedPayments = useMemo(() => {
    const start = (paymentsPage - 1) * pageSize;
    return filteredPayments.slice(start, start + pageSize);
  }, [filteredPayments, paymentsPage, pageSize]);

  // Counts for status chips
  const pendingCount = useMemo(
    () => (paymentSummary ? paymentSummary.pendingCount : purchaseEntries.filter((e) => e.status === 'PENDING').length),
    [purchaseEntries, paymentSummary]
  );
  const paidCount = useMemo(
    () => (paymentSummary ? paymentSummary.paidCount : purchaseEntries.filter((e) => e.status === 'PAID').length),
    [purchaseEntries, paymentSummary]
  );
  const partialCount = useMemo(
    () => (paymentSummary ? paymentSummary.partialCount : purchaseEntries.filter((e) => e.status === 'PARTIAL').length),
    [purchaseEntries, paymentSummary]
  );

  /**
   * Toggles selection of a single purchase entry row
   */
  const handleToggleSelectRow = (id) => {
    setSelectedEntryIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  /**
   * Toggles selection of all visible purchase entry rows
   */
  const handleToggleSelectAll = () => {
    if (selectedEntryIds.length === paginatedEntries.length && paginatedEntries.length > 0) {
      setSelectedEntryIds([]);
    } else {
      setSelectedEntryIds(paginatedEntries.map((e) => e.id));
    }
  };

  /**
   * Total calculated payment amount for selected entries
   */
  const totalSelectedAmount = useMemo(() => {
    return purchaseEntries
      .filter((e) => selectedEntryIds.includes(e.id))
      .reduce((sum, e) => sum + (e.amount || 0), 0);
  }, [purchaseEntries, selectedEntryIds]);

  /**
   * Helper function to format cleared payment amount
   */
  const formatClearedAmount = (val) => {
    if (val === undefined || val === null) return '0';
    const num = Number(val);
    if (isNaN(num)) return '0';
    return num % 1 === 0 ? num.toString() : num.toFixed(2);
  };

  // ─── Backend API Integration ───────────────────────────────────────────────
  const fetchEntries = async () => {
    if (!resolvedSupplierId) {
      setPurchaseEntries([]);
      return;
    }
    try {
      const res = await axios.get(ENDPOINTS.GET_SUPPLIER_PURCHASE_ENTRIES(resolvedSupplierId), {
        params: {
          page: 0,
          size: 100,
          sortBy: 'entryDate',
          sortDirection: 'desc'
        }
      });
      if (res.data) {
        const rawList = Array.isArray(res.data) ? res.data : (res.data.content || []);
        const mapped = rawList.map((e) => ({
          id: e.id,
          date: e.entryDate,
          invoiceDate: e.invoiceDate,
          grnNo: e.grnNo,
          invoiceNumber: e.invoiceNumber,
          amount: parseFloat(e.amount) || 0,
          clearedAmount: parseFloat(e.clearedAmount) || 0,
          status: e.status
        }));
        setPurchaseEntries(mapped);
      } else {
        setPurchaseEntries([]);
      }
    } catch (err) {
      console.warn("Could not fetch purchase entries from backend API", err);
      setPurchaseEntries([]);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(ENDPOINTS.GET_SUPPLIER_PAYMENT_HISTORY(resolvedSupplierId));
      if (res.data && res.data.content) {
        const mapped = res.data.content.map((p) => ({
          id: `PAY-${p.id}`,
          date: p.paymentDate ? p.paymentDate.slice(0, 10) : '',
          supplierName: supplierName,
          amount: parseFloat(p.amountPaid) || 0,
          mode: p.paymentMode,
          referenceNo: p.referenceNo || 'N/A',
          notes: p.notes
        }));
        setPaymentsHistory(mapped);
      }
    } catch (err) {}
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(ENDPOINTS.GET_SUPPLIER_PAYMENT_SUMMARY(resolvedSupplierId));
      if (res.data) {
        setPaymentSummary(res.data);
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchEntries();
    fetchHistory();
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedSupplierId]);

  /**
   * Refreshes the purchase entries view with spin animation
   */
  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchEntries();
    fetchHistory();
    fetchSummary();
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  /**
   * Handles payment submission from the modal
   */
  const handleConfirmPayment = async (e) => {
    e.preventDefault();
    const payAmt = parseFloat(paymentForm.amount) || totalSelectedAmount;
    if (payAmt <= 0) {
      toast.warning('Please enter a valid payment amount.');
      return;
    }

    try {
      await axios.post(ENDPOINTS.RECORD_SUPPLIER_PAYMENT(resolvedSupplierId), {
        amount: payAmt,
        paymentMode: paymentForm.paymentMode,
        referenceNo: paymentForm.referenceNo,
        notes: paymentForm.notes,
        purchaseEntryIds: selectedEntryIds.filter((id) => typeof id === 'number')
      });

      toast.success(`Payment of ₹${payAmt.toFixed(2)} recorded successfully!`);
      setIsMakePaymentModalOpen(false);
      setPaymentForm({ amount: '', paymentMode: 'Bank Transfer', referenceNo: '', notes: '' });
      setSelectedEntryIds([]);
      fetchEntries();
      fetchHistory();
      fetchSummary();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || "Failed to record payment on the server.";
      toast.error(`Payment error: ${errorMsg}`);
    }
  };

  return (
    <div className="supplier-payment-page-container">
      {/* ─── 1. Top Breadcrumb & Navigation Bar ────────────────────────────── */}
      <div className="supplier-payment-header-bar">
        <div className="supplier-payment-breadcrumb">
          <span
            className="supplier-payment-breadcrumb-link"
            onClick={() => navigate('/pharmacy/dashboard')}
          >
            Dashboard
          </span>
          <span className="supplier-payment-breadcrumb-separator">&gt;</span>
          <span
            className="supplier-payment-breadcrumb-link"
            onClick={() => navigate('/pharmacy/suppliers')}
          >
            suppliers
          </span>
          <span className="supplier-payment-breadcrumb-separator">&gt;</span>
          <span className="supplier-payment-breadcrumb-pill">pharmacySupplierPayment</span>
        </div>

        <div className="supplier-payment-nav-actions">
          <button
            type="button"
            className={`supplier-payment-btn-refresh ${isRefreshing ? 'spinning' : ''}`}
            onClick={handleRefresh}
            title="Refresh payment entries"
            aria-label="Refresh"
          >
            <FaSyncAlt />
          </button>
          <button
            type="button"
            className="supplier-payment-btn-back"
            onClick={() => navigate(-1)}
            title="Go back"
          >
            <FaChevronLeft style={{ fontSize: '0.8rem' }} /> Back
          </button>
        </div>
      </div>

      {/* ─── 2. Active Supplier Banner ─────────────────────────────────────── */}
      <div className="supplier-payment-supplier-banner">
        <span className="supplier-banner-highlight">{supplierName}</span>
      </div>

      {/* ─── 3. Unified Master Enterprise Card (OP Architecture) ───────────── */}
      <div className="unified-master-card">
        {/* Accent Bar */}
        <div className="master-card-accent-bar" />

        {/* Master Toolbar Header: Fused Search + Calendar Picker + Payment Summary */}
        <div className="master-toolbar-header">
          <div className="toolbar-left-group">
            {/* Fused Search Box */}
            <div className="toolbar-search-box">
              <div className="fused-search-group">
                <select
                  value={searchType}
                  onChange={(e) => {
                    setSearchType(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="GRN No">GRN No</option>
                  <option value="Invoice No">Invoice No</option>
                </select>
                <div className="search-input-wrapper">
                  <input
                    type="text"
                    placeholder={`Enter ${searchType}...`}
                    value={searchValue}
                    onChange={(e) => {
                      setSearchValue(e.target.value);
                      setPage(1);
                    }}
                  />
                  {searchValue && (
                    <button
                      type="button"
                      className="search-clear-btn"
                      onClick={() => setSearchValue('')}
                      title="Clear search"
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Common Calendar Utility: DateRangePickerComponent */}
            <div className="toolbar-date-picker">
              <DateRangePickerComponent
                startDate={startDate}
                endDate={endDate}
                onDateChange={handleDateChange}
              />
            </div>
          </div>

          {/* Right Summary and Payment CTA */}
          <div className="toolbar-right-group">
            <div className="supplier-payment-stat-box">
              <span className="stat-box-value">{purchaseEntries.length}</span>
              <span className="stat-box-label">Total Purchase Entries</span>
            </div>

            <div className="supplier-payment-summary-box">
              <span className="payment-summary-amount">
                ₹ {totalSelectedAmount.toFixed(2)}
              </span>
              <button
                type="button"
                className="master-new-appt-btn btn-payment-cta"
                onClick={() => setIsMakePaymentModalOpen(true)}
              >
                <FaCreditCard /> Make Payment
              </button>
            </div>
          </div>
        </div>

        {/* Master Tabs Bar: Purchase Entries & Payments History */}
        <div className="master-tabs-bar">
          <div className="tabs-list">
            <button
              type="button"
              className={`tab-btn ${activeTab === 'purchase_entries' ? 'active-tab-btn' : ''}`}
              onClick={() => {
                setActiveTab('purchase_entries');
                setPage(1);
              }}
            >
              Purchase Entries ({purchaseEntries.length})
            </button>
            <button
              type="button"
              className={`tab-btn ${activeTab === 'payments' ? 'active-tab-btn' : ''}`}
              onClick={() => {
                setActiveTab('payments');
                setPaymentsPage(1);
              }}
            >
              Payments History ({paymentsHistory.length})
            </button>
          </div>

          <div className="status-chips-list">
            <span className="status-chip chip-pending">Pending: {pendingCount}</span>
            <span className="status-chip chip-paid">Paid: {paidCount}</span>
            {partialCount > 0 && (
              <span className="status-chip chip-partial">Partial: {partialCount}</span>
            )}
          </div>
        </div>

        {/* ─── 4. Master Data Table ────────────────────────────────────────── */}
        <div className="master-table-container">
          {activeTab === 'purchase_entries' ? (
            <table className="master-data-table">
              <thead>
                <tr>
                  <th style={{ width: '44px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={
                        selectedEntryIds.length === paginatedEntries.length &&
                        paginatedEntries.length > 0
                      }
                      onChange={handleToggleSelectAll}
                    />
                  </th>
                  <th>DATE</th>
                  <th>INVOICE DATE</th>
                  <th>GRN NO</th>
                  <th>INVOICE NUMBER</th>
                  <th>PAYMENT AMOUNT</th>
                  <th>PAYMENT STATUS</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEntries.length > 0 ? (
                  paginatedEntries.map((entry) => {
                    const isSelected = selectedEntryIds.includes(entry.id);
                    return (
                      <tr
                        key={entry.id}
                        className={`table-data-row ${isSelected ? 'row-selected' : ''}`}
                        onClick={() => handleToggleSelectRow(entry.id)}
                      >
                        <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelectRow(entry.id)}
                          />
                        </td>
                        <td>{entry.date}</td>
                        <td>{entry.invoiceDate}</td>
                        <td style={{ fontWeight: 600, color: '#0284c7' }}>{entry.grnNo}</td>
                        <td>{entry.invoiceNumber}</td>
                        <td>
                          <div className="payment-amount-cell">
                            <span className="payment-main-amount">
                              ₹{entry.amount.toFixed(2)}
                            </span>
                            <span className="payment-cleared-amount">
                              [cleared Amount - {formatClearedAmount(entry.clearedAmount)} Rs]
                            </span>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`payment-status-badge ${
                              entry.status === 'PAID'
                                ? 'status-paid'
                                : entry.status === 'PARTIAL'
                                ? 'status-partial'
                                : 'status-pending'
                            }`}
                          >
                            {entry.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="supplier-payment-empty-cell">
                      <div className="empty-message-wrap">
                        <p>No Purchase Entries Found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            /* Payments History View */
            <table className="master-data-table">
              <thead>
                <tr>
                  <th>PAYMENT ID</th>
                  <th>PAYMENT DATE</th>
                  <th>PAYMENT MODE</th>
                  <th>REFERENCE NO</th>
                  <th>AMOUNT PAID</th>
                </tr>
              </thead>
              <tbody>
                {paginatedPayments.length > 0 ? (
                  paginatedPayments.map((pay) => (
                    <tr key={pay.id} className="table-data-row">
                      <td style={{ fontWeight: 600, color: '#0284c7' }}>{pay.id}</td>
                      <td>{pay.date}</td>
                      <td>
                        <span className="payment-mode-tag">{pay.mode}</span>
                      </td>
                      <td>{pay.referenceNo}</td>
                      <td style={{ fontWeight: 700, color: '#16a34a' }}>
                        ₹{pay.amount.toFixed(2)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="supplier-payment-empty-cell">
                      <div className="empty-message-wrap">
                        <p>No Payment Records Found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Master Pagination Footer (Material-UI Standard) */}
        <div className="master-pagination-footer">
          <span className="pagination-summary-text">
            Showing page{' '}
            <strong>
              {activeTab === 'purchase_entries' ? page : paymentsPage}
            </strong>{' '}
            of{' '}
            <strong>
              {activeTab === 'purchase_entries' ? totalPages : paymentsTotalPages}
            </strong>{' '}
            (
            {activeTab === 'purchase_entries'
              ? filteredEntries.length
              : filteredPayments.length}{' '}
            records)
          </span>
          <Pagination
            count={activeTab === 'purchase_entries' ? totalPages : paymentsTotalPages}
            page={activeTab === 'purchase_entries' ? page : paymentsPage}
            onChange={(e, value) => {
              if (activeTab === 'purchase_entries') setPage(value);
              else setPaymentsPage(value);
            }}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                components={{
                  previous: () => <span>Previous</span>,
                  next: () => <span>Next</span>,
                }}
              />
            )}
            color="primary"
            variant="outlined"
            shape="rounded"
          />
        </div>
      </div>

      {/* ─── 5. Make Payment Modal Dialog ──────────────────────────────────── */}
      {isMakePaymentModalOpen && (
        <div
          className="supplier-payment-modal-overlay"
          onClick={() => setIsMakePaymentModalOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="supplier-payment-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <FaFileInvoiceDollar style={{ color: '#0284c7', fontSize: '1.2rem' }} />
                <h3>Record Supplier Payment</h3>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setIsMakePaymentModalOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleConfirmPayment} className="modal-form-body">
              <div className="modal-supplier-info">
                <span className="supplier-lbl">Supplier:</span>
                <strong className="supplier-val">{supplierName}</strong>
              </div>

              <div className="modal-field">
                <label>Payment Amount (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  placeholder={totalSelectedAmount > 0 ? totalSelectedAmount.toString() : 'Enter amount'}
                  value={paymentForm.amount || (totalSelectedAmount > 0 ? totalSelectedAmount : '')}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, amount: e.target.value }))
                  }
                />
              </div>

              <div className="modal-field">
                <label>Payment Mode</label>
                <select
                  value={paymentForm.paymentMode}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, paymentMode: e.target.value }))
                  }
                >
                  <option value="Bank Transfer">Bank Transfer (NEFT / RTGS)</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Cash">Cash</option>
                  <option value="UPI">UPI / Digital</option>
                </select>
              </div>

              <div className="modal-field">
                <label>Reference / Cheque / UTR No</label>
                <input
                  type="text"
                  placeholder="e.g. UTR-88912739812"
                  value={paymentForm.referenceNo}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, referenceNo: e.target.value }))
                  }
                />
              </div>

              <div className="modal-field">
                <label>Notes / Remarks (Optional)</label>
                <textarea
                  rows="2"
                  placeholder="Payment notes or remarks..."
                  value={paymentForm.notes}
                  onChange={(e) =>
                    setPaymentForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="master-secondary-btn"
                  onClick={() => setIsMakePaymentModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="master-new-appt-btn">
                  Confirm Payment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacySupplierPayment;
